import type { SettingView, SystemInfo } from '@atlas-control/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { Check, KeyRound, UploadCloud, X } from 'lucide-react';
import { useState } from 'react';
import { DisabledReason } from '@/components/disabled-reason';
import { PageHeader } from '@/components/page-header';
import { StatusBadge } from '@/components/status-badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { api } from '@/lib/api';
import { formatDateTime } from '@/lib/format';
import { guard } from '@/lib/guards';
import { toastError, toastSuccess } from '@/lib/notify';

export const Route = createFileRoute('/_app/settings')({
  beforeLoad: ({ context }) => guard(context.me, 'settings.manage'),
  component: SettingsPage,
});

const CAPABILITY_LABELS: Record<string, string> = {
  pauseResume: 'Pause and resume a run',
  cancel: 'Cancel a run',
  mergeLintEdit: 'Final-review edits update the client’s board',
  keyReload: 'Settings and keys sent to Atlas',
};

function SettingsPage() {
  const qc = useQueryClient();
  const settings = useQuery({
    queryKey: ['settings'],
    queryFn: () => api<{ settings: SettingView[] }>('/settings').then((r) => r.settings),
  });
  const system = useQuery({ queryKey: ['system'], queryFn: () => api<SystemInfo>('/system') });
  const [editing, setEditing] = useState<SettingView | null>(null);
  const push = useMutation({
    mutationFn: () => api<{ settings: SettingView[] }>('/settings/push', { method: 'POST' }),
    onSuccess: (r) => {
      qc.setQueryData(['settings'], r.settings);
      toastSuccess('Sent to Atlas; its services restart with the new values');
    },
    onError: (e) => toastError(e),
  });

  const unpushed = (settings.data ?? []).filter((s) => s.isSet && !s.pushedToAtlasAt).length;
  const canReload = Boolean(system.data?.atlas.capabilities?.keyReload);
  const groups = [...new Set((settings.data ?? []).map((s) => s.group))];

  return (
    <>
      <PageHeader
        title="Settings"
        actions={
          <DisabledReason
            reason={
              !canReload
                ? 'Atlas cannot reload settings yet (pending P3): apply changes in Atlas’s env file and restart it'
                : unpushed === 0
                  ? 'Nothing to send'
                  : null
            }
          >
            <Button
              disabled={!canReload || unpushed === 0 || push.isPending}
              onClick={() => push.mutate()}
            >
              <UploadCloud /> Send {unpushed || ''} to Atlas and reload
            </Button>
          </DisabledReason>
        }
      />

      <div className="grid gap-6 lg:grid-cols-3">
        <div className="space-y-6 lg:col-span-2">
          {groups.map((g) => (
            <Card key={g}>
              <CardHeader>
                <CardTitle className="text-base">{g}</CardTitle>
              </CardHeader>
              <CardContent className="divide-y">
                {(settings.data ?? [])
                  .filter((s) => s.group === g)
                  .map((s) => (
                    <div
                      key={s.name}
                      className="flex flex-wrap items-center justify-between gap-3 py-3"
                    >
                      <div className="min-w-0">
                        <p className="font-medium">{s.label}</p>
                        <p className="font-mono text-xs text-muted-foreground">{s.name}</p>
                        {s.updatedAt && (
                          <p className="text-xs text-muted-foreground">
                            Changed {formatDateTime(s.updatedAt)}
                            {s.updatedBy ? ` by ${s.updatedBy.name}` : ''}
                          </p>
                        )}
                        {s.pushError && <p className="text-xs text-danger">{s.pushError}</p>}
                      </div>
                      <div className="flex items-center gap-2">
                        {s.isSet ? (
                          <code className="rounded bg-muted px-2 py-0.5 text-xs">
                            {s.secret ? `••••${s.last4 ?? ''}` : s.value}
                          </code>
                        ) : (
                          <span
                            className="text-xs text-muted-foreground"
                            title="Atlas keeps using the value in its own env file, if it has one. The panel cannot read that file; Atlas does not report its keys yet (A7)."
                          >
                            Not managed here · Atlas uses its own
                          </span>
                        )}
                        {s.isSet && (
                          <StatusBadge tone={s.pushedToAtlasAt ? 'success' : 'warning'}>
                            {s.pushedToAtlasAt ? 'In Atlas' : 'Not sent'}
                          </StatusBadge>
                        )}
                        <Button variant="outline" size="sm" onClick={() => setEditing(s)}>
                          <KeyRound /> {s.secret ? 'Rotate' : 'Change'}
                        </Button>
                      </div>
                    </div>
                  ))}
              </CardContent>
            </Card>
          ))}
        </div>

        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Atlas</CardTitle>
              <CardDescription className="font-mono text-xs">
                {system.data?.atlas.baseUrl}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3 text-sm">
              <p>
                {system.data?.atlas.reachable ? (
                  <StatusBadge tone="success">Reachable</StatusBadge>
                ) : (
                  <StatusBadge tone="danger">Not reachable</StatusBadge>
                )}
              </p>
              <ul className="space-y-1.5">
                {Object.entries(CAPABILITY_LABELS).map(([k, label]) => {
                  const ok = system.data?.atlas.capabilities?.[k];
                  return (
                    <li key={k} className="flex items-center gap-2">
                      {ok ? (
                        <Check className="size-4 text-success" />
                      ) : (
                        <X className="size-4 text-muted-foreground" />
                      )}
                      <span className={ok ? '' : 'text-muted-foreground'}>{label}</span>
                    </li>
                  );
                })}
              </ul>
              {system.data?.atlas.capabilities &&
              Object.keys(CAPABILITY_LABELS).every((k) => system.data?.atlas.capabilities?.[k]) ? (
                <p className="text-xs text-muted-foreground">
                  Atlas supports everything the panel uses. Runs started from the panel are never
                  emailed by Atlas, and a run waiting on staff has no deadline.
                </p>
              ) : (
                <p className="text-xs text-muted-foreground">
                  An unchecked item is not in this Atlas yet; the panel turns it on as soon as Atlas
                  publishes it.
                </p>
              )}
            </CardContent>
          </Card>
          <TestEmailCard />
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Panel</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 text-sm">
              <p>
                Version <span className="font-mono">{system.data?.appVersion}</span> ·{' '}
                {system.data?.migrations} migrations
              </p>
              <table className="w-full text-xs">
                <thead className="text-muted-foreground">
                  <tr>
                    <th className="text-left font-medium">Job</th>
                    <th className="text-right font-medium">Queued</th>
                    <th className="text-right font-medium">Active</th>
                    <th className="text-right font-medium">Failed</th>
                  </tr>
                </thead>
                <tbody>
                  {system.data?.jobs.map((j) => (
                    <tr key={j.name}>
                      <td className="font-mono">{j.name}</td>
                      <td className="text-right tabular-nums">{j.queued}</td>
                      <td className="text-right tabular-nums">{j.active}</td>
                      <td className={`text-right tabular-nums ${j.failed ? 'text-danger' : ''}`}>
                        {j.failed}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent>
          </Card>
        </div>
      </div>

      <EditDialog setting={editing} onClose={() => setEditing(null)} />
    </>
  );
}

function EditDialog({ setting, onClose }: { setting: SettingView | null; onClose: () => void }) {
  const qc = useQueryClient();
  const [value, setValue] = useState('');
  const save = useMutation({
    mutationFn: () =>
      api<{ settings: SettingView[] }>(`/settings/${setting?.name}`, {
        method: 'PUT',
        body: { value },
      }),
    onSuccess: (r) => {
      qc.setQueryData(['settings'], r.settings);
      toastSuccess('Saved. Send it to Atlas to take effect.');
      setValue('');
      onClose();
    },
    onError: (e) => toastError(e),
  });
  if (!setting) return null;
  return (
    <Dialog open onOpenChange={(o) => !o && (setValue(''), onClose())}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {setting.secret ? 'Rotate' : 'Change'} {setting.label}
          </DialogTitle>
          <DialogDescription>
            {setting.secret
              ? 'Paste the new key. It is stored encrypted and will not be shown again.'
              : `Current value: ${setting.value ?? 'not set'}`}
          </DialogDescription>
        </DialogHeader>
        <Input
          type={setting.secret ? 'password' : 'text'}
          autoComplete="off"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          aria-label={setting.label}
          autoFocus
        />
        <DialogFooter>
          <Button disabled={!value.trim() || save.isPending} onClick={() => save.mutate()}>
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Checks mail delivery (sender domain, Resend key) with a plain message to any address. */
function TestEmailCard() {
  const [to, setTo] = useState('');
  const send = useMutation({
    mutationFn: () =>
      api<{ providerMessageId: string }>('/system/test-email', { method: 'POST', body: { to } }),
    onSuccess: (r) => toastSuccess(`Sent (message ${r.providerMessageId})`),
    onError: (e) => toastError(e),
  });
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Test email</CardTitle>
        <CardDescription>
          Sends “read this email you asked for” through the panel’s mailer.
        </CardDescription>
      </CardHeader>
      <CardContent className="flex gap-2">
        <Input
          type="email"
          placeholder="you@knowaiclub.com"
          value={to}
          onChange={(e) => setTo(e.target.value)}
          aria-label="Test email address"
        />
        <Button
          variant="outline"
          disabled={!to.includes('@') || send.isPending}
          onClick={() => send.mutate()}
        >
          Send
        </Button>
      </CardContent>
    </Card>
  );
}
